// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { RowComponent, Tabulator } from 'tabulator-tables';
//Image
import VMS_SK_GYEONGJU_PED from '@/assets/skin_image/VMS_SK_GYEONGJU_PED.png';
// Modal Components
// import ModalBoardOperation from '@/domains/scenario/components/ModalBoardOperation'; // 전광판 비활성
import ModalSpeakerOperation from '@/domains/scenario/components/ModalSpeakerOperation';
import useDeviceManagement from '@/domains/scenario/hooks/useDeviceManagement'; // Updated local import name
// Custom Hooks
import type { DeviceOperationUpdateDOMRequest } from '@/domains/scenario/types/device-management-types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// UI Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import { URLS } from '@/shared/config/urls';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import ModalProjector from '../../components/ModalProjector';

const commonColumnSettings = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// device-operation endpoint allows: id (device_operation_id) | updated_time.
const DEVICE_SORT_FIELD_MAP: Record<string, string> = {
  device_operation_id: 'id',
  updated_time: 'updated_time',
};

const DeviceManagementPage = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalProjector, setModalProjector] = useState(false);

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const [disabledForm, setDisabledForm] = useState(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState(false);
  const [hasChangesCreate, setHasChangesCreate] = useState(false);
  const [isNewClicked, setIsNewClicked] = useState(false);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const isNewClickedRef = useRef(isNewClicked);
  const [newId, setNewId] = useState<number | string>('');

  const [selectedDevice, setSelectedDevice] = useState<{
    device_operation_id: number | string | null;
  }>({
    device_operation_id: null,
  });

  const selectedDeviceRef = useRef(selectedDevice);
  useEffect(() => {
    selectedDeviceRef.current = selectedDevice;
  }, [selectedDevice]);

  const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
  const [isDeviceTypeSelected, setIsDeviceTypeSelected] = useState(false);

  //장치 기본 (공통) - 기본 입력폼
  interface DeviceInputFormState {
    device_operation_id: string | number;
    device_type: string;
    device_type_value: string;
    name: string;
    description: string;
    updated_time: string | null;
    is_device_not_set: boolean;
    mapped_scenario_name?: string | null;
  }

  const initialDeviceInputForm: DeviceInputFormState = {
    device_operation_id: '',
    device_type: '',
    device_type_value: '',
    name: '',
    description: '',
    updated_time: null,
    is_device_not_set: true, //vms, speaker설정 여부
  };
  //초기 전광판 입력폼
  interface VmsInputFormState {
    vms_skin_type: string;
    vms_dom: string;
    vms_text_orientation: string;
    vms_skin_option: string;
    vms_exposure_time: number | string;
    vms_text: string;
    vms_text_size: number | string;
    vms_text_color: string;
    vms_text_weight: string;
    vms_text2: string;
    vms_text2_size: number | string;
    vms_text2_color: string;
    vms_text2_weight: string;
    vms_text_position_x: number | string;
    vms_text_position_y: number | string;
    vms_width: number;
    vms_height: number;
    vms_background_color: string;
    vms_skin_option_image: string;
    vms_skin_image_path: string;
    vms_skin_image: string;
    vms_skin_file_name: string;
    vms_user_select: boolean;
  }

  const initialVmsInputForm: VmsInputFormState = {
    vms_skin_type: '',
    vms_dom: '',
    vms_text_orientation: 'VMS_TO_HOR', // 기본값: 가로 텍스트
    vms_skin_option: 'VMS_SK_NO', // 기본값: 스킨 없음
    vms_exposure_time: 3, // 기본값: 180초

    vms_text: '', // 텍스트1
    vms_text_size: 30, // 텍스트1 크기: 텍스트 크기 100
    vms_text_color: '#FFFFFF', //텍스트1 색깔 : 검은색
    vms_text_weight: 'normal', //텍스트1 굵기: 기본

    vms_text2: '', // 텍스트2
    vms_text2_size: 30, // 텍스트크기2: 텍스트 크기 100
    vms_text2_color: '#FFFFFF', //텍스트2 색깔 : 검은색
    vms_text2_weight: 'normal', //텍스트2 굵기: 기본

    vms_text_position_x: '', //내부박스의 중심점 x위치
    vms_text_position_y: '', //내부박스의 중심점 y위치

    vms_width: 288, // 기본값
    vms_height: 96, // 기본값
    vms_background_color: '#FFFFFF',

    vms_skin_option_image: '',
    vms_skin_image_path: '', // 기본값: 이미지 경로 없음
    vms_skin_image: '', // 기본값: 이미지 경로 없음
    vms_skin_file_name: '', //읽어들인 스킨 이미지 이름
    vms_user_select: false,
  };

  interface SpeakerScheduleItem {
    id?: string;
    is_new?: boolean;
    volume: number;
    start_hour: number;
    start_minute: number;
    end_hour: number;
    end_minute: number;
  }

  interface SpeakerInputFormState {
    speaker_file: string;
    speaker_file_path: string;
    speaker_schedule: SpeakerScheduleItem[];
  }

  const initialSpeakerInputForm: SpeakerInputFormState = {
    speaker_file: Math.random() < 0.5 ? 'SPK_FL_A' : 'SPK_FL_B', // 랜덤으로 'SPK_FL_A' 또는 'SPK_FL_B' 설정
    speaker_file_path: '', // 기본값: 파일 경로 없음
    speaker_schedule: [], // 기본값: 빈 배열 (스케줄 없음)
  };

  const initialSpeakerScheduleInputFormList: SpeakerScheduleItem[] = [
    /*{
            volume: 50,
            start_hour: 0,
            start_minute: 0,
            end_hour: 18,
            end_minute: 59 }*/
  ];

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked]);

  const [deviceInputForm, setDeviceInputForm] = useState(initialDeviceInputForm);
  const [vmsInputForm, setVmsInputForm] = useState(initialVmsInputForm);
  const [speakerInputForm, setSpeakerInputForm] = useState(initialSpeakerInputForm);
  const [speakerScheduleInputFormList, setSpeakerScheduleInputFormList] = useState(
    initialSpeakerScheduleInputFormList
  );

  const [optionParams, _setOptionParams] = useState('upper-code=221&upper-code=VMS_SK_OPTION');

  const { commonListData } = useCommonCodes({ optionParams });

  //검색 필터에 사용할 옵션 - 전체,전광판,스피커
  const deviceTypeFilteroptions = [
    // TODO: i18n - hardcoded Korean string
    { value: '', label: '전체' }, // 기본값(전체)
    ...(commonListData?.[221]?.code || []).map((code, index) => ({
      value: code ?? '',
      label: commonListData?.[221]?.name[index] || '',
    })),
  ];

  //입력에 사용할 옵션 - 스피커, 로고젝터
  // 전광판(221002), 221004는 프론트 생성 폼에서 제외
  const deviceTypeFormoptions = [
    ...(commonListData?.[221]?.code || [])
      .map((code, index) => ({
        value: code ?? '',
        label: commonListData?.[221]?.name[index] || '',
      }))
      .filter((opt) => opt.value !== '221002' && opt.value !== '221004')
      .sort((a, b) => (b.value ?? '').localeCompare(a.value ?? '')),
  ];

  const _deviceList = commonListData?.['221'];

  const skinOptionInfo = useMemo(() => {
    return (
      commonListData?.VMS_SK_OPTION?.code?.map((code, index) => ({
        label: commonListData?.VMS_SK_OPTION?.name[index] || '',
        value: code,
        description: commonListData?.VMS_SK_OPTION?.description[index] || '',
      })) ?? []
    );
  }, [commonListData]);

  const emptyDetail = () => {
    setDeviceInputForm(initialDeviceInputForm);
    setVmsInputForm(initialVmsInputForm);
    setSpeakerInputForm(initialSpeakerInputForm);
    setSpeakerScheduleInputFormList(initialSpeakerScheduleInputFormList);
    setIsDeviceTypeSelected(false);
  };

  //Button
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUpdateButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  // const enableCreateButtons = () => {
  //   disableAllButtons();
  //   setButtonState((prevState) => ({
  //     ...prevState,
  //     create: true,
  //   }));
  // };

  const enableCancelButtons = () => {
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    //setSelectedSiteId(null)
    setSelectedDevice({ device_operation_id: null });
  };

  // const createCallback = () => {
  //   enableInitialButtons();
  //   emptyDetail();
  //   setIsRequired(false);
  //   setDisabledForm(true);
  //   setDisabledId(true);
  //   setHasChangesCreate(false);
  //   setSelectedDevice({ device_operation_id: null });
  // };

  const table = useServerTable<{ device_type?: string; input?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const deviceOperationListQueryParams = table.queryString;

  const {
    deviceOperationListData,
    detailDeviceOperationData, // Renamed from detailDeviceOprationData
    createDeviceOperation: _createDeviceOperation,
    updateDeviceOperation: _updateDeviceOperation,
    deleteDeviceOperation,
    createDeviceOperationAndDom,
    updateDeviceOperationAndDom,
  } = useDeviceManagement({
    deviceOperationId: selectedDevice?.device_operation_id,
    deviceOperationListQueryParams: deviceOperationListQueryParams,
    onUpdateSuccess: (_responseData) => {
      // reloadCallback();
      // updateCallback();
    },
    onDeleteSuccess: reloadCallback,
    onCreateSuccess: (responseData) => {
      reloadCallback();
      //alert('생성 성공!')
      //createCallback()
      const newDeviveOperationId = responseData.device_operation_id;
      //alert('생성된 아이디:'+newDeviveOperationId)
      setNewId(newDeviveOperationId);

      setSelectedDevice({
        device_operation_id: newDeviveOperationId,
      });

      // emptyDetail();
      // setDisabledForm(true);
      // enableInitialButtons();
    },
  });

  const devicePageData = deviceOperationListData?.data;
  const deviceRows = devicePageData?.rows ?? [];
  const deviceColumns: ColumnDefinition[] = useMemo(
    () => [
      {
        title: 'No',
        // Reverse numbering (newest row shows the highest), using the server total.
        formatter: (cell) => {
          const total = devicePageData?.total ?? 0;
          const globalIndex = (table.page - 1) * table.size + cell.getRow().getPosition(true);
          return String(total - globalIndex + 1);
        },
        width: 65,
        headerSort: false,
        ...commonColumnSettings,
      },
      // TODO: i18n - hardcoded Korean string
      {
        title: '기기 동작 ID',
        field: 'device_operation_id',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
      {
        title: '기기 유형',
        field: 'device_type',
        widthGrow: 0.5,
        headerSort: false,
        ...commonColumnSettings,
        formatter: (cell) => {
          const value = cell.getValue() as number;
          const typeMap: Record<number, string> = {
            221001: '스피커',
            // 221002: '전광판',
            221003: '로고젝터',
          };
          return typeMap[value] || '알 수 없음'; // fallback 처리
        },
      },
      {
        title: '동작명',
        field: 'name',
        widthGrow: 1.5,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '동작설명',
        field: 'description',
        widthGrow: 2,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
    ],
    [devicePageData?.total, table.page, table.size]
  );

  const logData = detailDeviceOperationData?.data // Changed property access
    ? [
        {
          label: t('cmn > registered by'),
          value: detailDeviceOperationData?.data.registered_by || '', // Changed property access
        },
        {
          label: t('cmn > registered time'),
          value: detailDeviceOperationData?.data.registered_time || '', // Changed property access
        },
        {
          label: t('cmn > updated by'),
          value: detailDeviceOperationData?.data.updated_by || '', // Changed property access
        },
        {
          label: t('cmn > updated time'),
          value: detailDeviceOperationData?.data.updated_time || '', // Changed property access
        },
      ]
    : [];

  const extractFileNameFromPath = useCallback((path: string | null | undefined) => {
    if (!path) return '';

    const parts = path.split('/');
    return decodeURIComponent(parts[parts.length - 1] ?? '');
  }, []);

  //상세보기에서 가져온 이미지경로로 이미지를 가져와서 base64로 변환 (수정시에 사용)
  const convertImageUrlToBase64 = useCallback((url: string, callback: (v: string) => void) => {
    const img = new Image();
    img.crossOrigin = 'use-credentials';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        callback('');
        return;
      }

      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      callback(dataURL);
    };

    img.onerror = (err) => {
      // TODO: i18n - hardcoded Korean string
      console.error('이미지를 base64로 변환하는 중 오류 발생:', err);
      callback('');
    };

    img.src = url;
  }, []);

  interface SkinOptionObject {
    width?: number;
    height?: number;
    backgroundColor?: string;
    backgroundImage?: string;
    textDirection?: 'horizontal' | 'vertical';
    textPosition?: {
      x: number;
      y: number;
    };
  }

  type SkinOptionInput = SkinOptionObject | string | null;

  interface UpdatedValues {
    vms_width?: number;
    vms_height?: number;
    vms_background_color?: string;
    vms_text_orientation?: string;
    vms_text_position_x?: number | string;
    vms_text_position_y?: number | string;
  }

  const setSkinOptionInfo = useCallback((obj: SkinOptionInput) => {
    if (!obj) return;

    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj) as SkinOptionObject;
      } catch (e) {
        console.error('JSON parsing failed:', e);
        return;
      }
    }

    const updatedValues: UpdatedValues = {};

    if (obj.width !== undefined) {
      updatedValues.vms_width = obj.width;
    }

    if (obj.height !== undefined) {
      updatedValues.vms_height = obj.height;
    }

    if (obj.backgroundColor !== undefined) {
      updatedValues.vms_background_color = obj.backgroundColor;
    }

    if (obj.backgroundImage !== undefined) {
      let imgAsset = '';
      if (obj.backgroundImage === 'VMS_SK_GYEONGJU_NORMAL') {
        imgAsset = VMS_SK_GYEONGJU_PED;
      }

      fetch(imgAsset)
        .then((res) => res.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Image = reader.result as string;
            setVmsInputForm((prev) => ({
              ...prev,
              vms_skin_option_image: base64Image,
            }));
          };
          reader.readAsDataURL(blob);
        })
        .catch((err) => {
          console.error('skin option image base64 fail', err);
          setVmsInputForm((prev) => ({
            ...prev,
            vms_skin_option_image: '',
          }));
        });
    } else {
      setVmsInputForm((prev) => ({
        ...prev,
        vms_skin_option_image: '',
      }));
    }

    if (obj.textDirection !== undefined) {
      let direction: string = obj.textDirection;
      if (direction === 'horizontal') direction = 'VMS_TO_HOR';
      if (direction === 'vertical') direction = 'VMS_TO_VER';
      updatedValues.vms_text_orientation = direction;
    }

    if (obj.textPosition !== undefined) {
      updatedValues.vms_text_position_x = obj.textPosition.x;
      updatedValues.vms_text_position_y = obj.textPosition.y;
    } else {
      updatedValues.vms_text_position_x = '';
      updatedValues.vms_text_position_y = '';
    }

    setVmsInputForm((prev) => ({
      ...prev,
      ...updatedValues,
    }));
  }, []);

  useEffect(() => {
    if (!detailDeviceOperationData) return;

    setDisabledForm(false);
    enableUpdateButtons();
    setIsNewClicked(false);
    setNewId('');
    setIsDeviceTypeSelected(true);

    const {
      device_operation_id = '',
      device_type = '',
      device_type_value = '',
      name = '',
      description = '',
      updated_time = '',
      mapped_scenario_name = '',
      vms_dom = '',
      vms_exposure_time = 0,
      vms_skin_option = '',
      vms_text_orientation = '',
      vms_text = '',
      vms_text_size = 0,
      vms_text_color = '',
      vms_text_weight = '',
      vms_text2 = '',
      vms_text2_size = 0,
      vms_text2_color = '',
      vms_text2_weight = '',
      vms_text_position_x: _vms_text_position_x = '',
      vms_text_position_y: _vms_text_position_y = '',
      vms_width: _vms_width = 0,
      vms_height: _vms_height = 0,
      vms_background_color: _vms_background_color = '',
      vms_skin_image_path = '',
      vms_skin_image = '',
      vms_skin_file_name: _vms_skin_file_name = '',
      speaker_file = '',
      speaker_file_path = '',
      speaker_schedule = [],
      registered_by: _registered_by = '',
      registered_time: _registered_time = '',
      updated_by: _updated_by = '',
    } = (detailDeviceOperationData.data || {}) as Record<string, any>;

    let is_device_not_set = false;
    let extractedFileNameFromVmsSkinImagePath = '';

    if (device_type === '221001') {
      is_device_not_set = !speaker_schedule || speaker_schedule.length === 0;
      // } else if (device_type === '221002') {
      //   if (vms_skin_option) {
      //     const selected = skinOptionInfo.find((option) => option.value === vms_skin_option);
      //     if (selected) {
      //       const obj = JSON.parse(selected.description);
      //       setSkinOptionInfo(obj);
      //     }
      //   }

      if (vms_skin_image_path) {
        const url = `${URLS.BACK_DSH}/common/image/${vms_skin_image_path}`;
        convertImageUrlToBase64(url, (base64Image) => {
          setVmsInputForm((prev) => ({
            ...prev,
            vms_skin_image: base64Image,
            vms_user_select: false,
          }));
        });
      }

      extractedFileNameFromVmsSkinImagePath = extractFileNameFromPath(vms_skin_image_path);
    }

    setDeviceInputForm({
      device_operation_id,
      device_type,
      device_type_value,
      name,
      description,
      updated_time,
      mapped_scenario_name,
      is_device_not_set,
    });

    setSpeakerInputForm({
      speaker_file,
      speaker_file_path,
      speaker_schedule,
    });

    setVmsInputForm((prev) => ({
      ...prev,
      vms_dom: vms_dom || '',
      vms_text_orientation: vms_text_orientation || '',
      vms_skin_option: vms_skin_option || '',
      vms_exposure_time: vms_exposure_time ?? '',
      vms_text: vms_text || '',
      vms_text_color: vms_text_color || '',
      vms_text_size: vms_text_size ?? '',
      vms_text_weight: vms_text_weight ?? '',
      vms_text2: vms_text2 || '',
      vms_text2_color: vms_text2_color || '',
      vms_text2_size: vms_text2_size ?? '',
      vms_text2_weight: vms_text2_weight ?? '',
      vms_skin_image_path: vms_skin_image_path ?? '',
      vms_skin_image: vms_skin_image ?? '',
      vms_skin_file_name: extractedFileNameFromVmsSkinImagePath ?? '',
    }));

    // TODO: i18n - hardcoded Korean string
    if (device_type_value === '스피커') {
      const updatedSpeakerSchedule = (speaker_schedule || []).map(
        (item: Record<string, unknown>) => ({
          ...item,
          is_new: false,
        })
      );
      setSpeakerScheduleInputFormList(updatedSpeakerSchedule);
    }
  }, [
    detailDeviceOperationData,
    enableUpdateButtons,
    convertImageUrlToBase64,
    extractFileNameFromPath,
  ]);

  const handleCloseModal = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('입력하신 내용이 저장되지 않습니다. 계속하시겠습니까?'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      //alert('종료!')

      setIsModalOpen(false);
      //setIsNewClicked(false);
      return;
    });
  };

  const handleSaveVmsSetting = () => {
    //vms 모달창 저장
    //alert('모달!')
    const { vms_text: _vms_text } = vmsInputForm;

    /*
        if (!vms_text || vms_text.trim() === "") {
            // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
            new NoticeMessage("먼저 문구를 입력해주세요");
            return;
        }
        */

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('정말로 저장하시겠습니까?'), {
      mode: 'confirm',
    });

    message.confirmClicked().then(() => {
      setIsModalOpen(false);
    });
  };

  const checkScheduleOverlap = (
    scheduleList: {
      start_hour: number;
      start_minute: number;
      end_hour: number;
      end_minute: number;
    }[]
  ) => {
    for (let i = 0; i < scheduleList.length; i++) {
      const s1 = scheduleList[i];
      if (!s1) continue;
      const start1 = convertToMinutes(s1.start_hour, s1.start_minute);
      const end1 = convertToMinutes(s1.end_hour, s1.end_minute);

      // 유효성 검사: 끝 시간이 시작 시간보다 빠르면 오류
      if (end1 <= start1) {
        return true;
      }

      for (let j = i + 1; j < scheduleList.length; j++) {
        const s2 = scheduleList[j];
        if (!s2) continue;
        const start2 = convertToMinutes(s2.start_hour, s2.start_minute);
        const end2 = convertToMinutes(s2.end_hour, s2.end_minute);

        if (end2 <= start2) {
          return true;
        }

        // 겹침 검사: 새로 들어오는 스케줄이 기존 스케줄 범위에 포함되는 경우
        const isStartInside = start2 >= start1 && start2 < end1;
        const isEndInside = end2 > start1 && end2 <= end1;
        const isWrapped = start2 <= start1 && end2 >= end1; // 완전히 감싸는 경우

        if (isStartInside || isEndInside || isWrapped) {
          return true;
        }
      }
    }
    return false;
  };

  // 시간을 분 단위로 변환하는 함수
  const convertToMinutes = (hour: number, minute: number) => {
    return hour * 60 + minute;
  };

  const handleSaveSpeakerSetting = () => {
    //speaker 모달창 저장
    if (!Array.isArray(speakerScheduleInputFormList) || speakerScheduleInputFormList.length === 0) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('먼저 음량스케줄을 등록해주세요');
      return;
    }

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('정말로 저장하시겠습니까?'), {
      mode: 'confirm',
    });

    message.confirmClicked().then(() => {
      //alert('진행!');

      const device_operation_id = deviceInputForm?.device_operation_id ?? '';
      const device_type = deviceInputForm?.device_type ?? '';
      const description = deviceInputForm?.description ?? '';
      const name = deviceInputForm?.name ?? 'TEST';
      const speaker_file = speakerInputForm?.speaker_file ?? '';
      const speaker_file_path = '';
      const speaker_schedule = (speakerScheduleInputFormList ?? []).map((item) => {
        const { is_new: _is_new, ...rest } = item; // is_new 제외하고 나머지 속성만 rest에 저장
        return rest; // is_new를 제외한 나머지 속성 반환
      });

      const isOverLapTime = checkScheduleOverlap(speaker_schedule);

      if (isOverLapTime === true) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        // TODO: i18n - hardcoded Korean string
        new NoticeMessage('음량 스케줄 시간을 중복하여 등록했습니다.');
        return;
      }

      const _commonObj = {
        device_operation_id,
        device_type,
        description,
        name,
        speaker_file,
        speaker_schedule,
      };

      setSpeakerInputForm({
        speaker_file,
        speaker_file_path,
        speaker_schedule,
      });

      if (isNewClicked === false) {
        // 수정 모드
        //alert('수정 진행!');
        //updateDeviceOperation(commonObj);
        //updateDeviceOperationAndDom(commonObj)
      } else {
        // 생성 모드
        //alert('생성 진행!');
        //createDeviceOperation(commonObj);
        //createDeviceOperationAndDom(commonObj);
      }

      //reloadCallback()

      setIsModalOpen(false);
    });
  };

  //vms 모달창의 입력을 받는 함수
  const handleVmsInputForm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    if (name === 'vms_text' || name === 'vms_text2') {
      setDeviceInputForm((prev) => ({
        ...prev,
        is_device_not_set: false,
      }));
    }

    if (name === 'vms_skin_image') {
      const file = (e.target as HTMLInputElement).files?.[0];

      if (file) {
        // 첫 번째 파일만

        const fileName = file.name;

        const reader = new FileReader();
        reader.onload = () => {
          const imageDataUrl = reader.result as string; // Type assertion

          const img = new Image();

          img.onerror = () => {
            // TODO: i18n - hardcoded Korean string
            console.error('이미지를 로드할 수 없습니다:', imageDataUrl);
          };

          img.src = imageDataUrl;

          setVmsInputForm((prev) => ({
            ...prev, // 기존 상태 유지
            vms_skin_image: imageDataUrl, // 또는 base64 URL, 필요에 따라
            vms_skin_file_name: fileName,
            vms_user_select: true, //사용자 이미지 선택여부 true -> 상세보기 이미지 교체
          }));
        };
        reader.readAsDataURL(file); // base64 형식으로 읽음
      }
    }

    if (name === 'vms_skin_option') {
      //alert('스킨옵션 선택!')
      //const obj=JSON.parse(value)
      //alert(obj)
      //setSkinOptionInfo(obj)

      const selected = skinOptionInfo.find((option) => option.value === value);

      if (selected) {
        //alert(selected.description);
        const obj = JSON.parse(selected.description);
        //alert(obj)
        setSkinOptionInfo(obj);
      } else {
        //alert("일치하는 스킨 옵션이 없습니다.");
        return;
      }
    }

    setVmsInputForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (isNewClicked === true) {
    } else {
      setHasChangesUpdate(true);
    }
  };

  const removeFile = () => {
    //alert('파일 제거')
    setVmsInputForm((prev) => ({
      ...prev, // 기존 상태 유지
      vms_skin_image: '',
      vms_skin_image_path: '',
      vms_skin_file_name: '', //읽어들인 스킨 이미지 이름
      vms_user_select: false, //사용자 선택 false
    }));
  };

  //스피커 스케줄 등록시 기기 등록상태 (미설정->미리보기)
  useEffect(() => {
    if (speakerInputForm.speaker_schedule?.length > 0) {
      setDeviceInputForm((prev) => ({
        ...prev,
        is_device_not_set: false,
      }));
    }
  }, [speakerInputForm.speaker_schedule]);

  //speaker 모달창의 입력을 받는 함수
  const handleSpeakerInputForm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    if (speakerInputForm.speaker_schedule.length !== 0) {
      setDeviceInputForm((prev) => ({
        ...prev,
        is_device_not_set: false,
      }));
    }

    if (index === -1) {
      //동적 박스 아니고 - 음성 파일 옵션임
      setSpeakerInputForm((prevState) => ({
        ...prevState, // 기존 값 복사
        speaker_file: value, // speaker_file만 업데이트
      }));
      return;
    }

    //start_hour, start_minue, end_hour, end_minute 값 검사

    // speakerScheduleInputFormList의 index번째 항목을 업데이트
    const updatedSpeakerScheduleList = [...speakerScheduleInputFormList]; // 기존 배열 복사
    const existingItem = updatedSpeakerScheduleList[index];
    if (!existingItem) return;
    updatedSpeakerScheduleList[index] = {
      ...existingItem, // 기존 값 유지
      [name]: parseInt(value, 10), // 해당 name 값을 새로운 value로 설정
    };

    setHasChangesUpdate(true);

    // 업데이트된 speakerScheduleInputFormList를 상태에 반영
    setSpeakerScheduleInputFormList(updatedSpeakerScheduleList);
  };

  //상단 검색 셀렉트 필터 - 전체, 스피커, 전광판
  const handleDeviceTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDeviceTypeFilter(e.target.value);
  };

  //
  const handleDeviceOperationAction = () => {
    //alert(device_type)
    //alert(device_type_value)
    setIsModalOpen(true);
  };

  //하단 등록 셀렉트 필터 - 전체, 스피커, 전광판
  const handleDeviceTypeFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { value, name } = e.target;
    if (name === 'device_type_form') {
      setIsDeviceTypeSelected(value === '221001' || value === '221003' || value === '221004');
      // TODO: i18n - hardcoded Korean string
      if (value === '221001') {
        setDeviceInputForm((prev) => ({
          ...prev,
          device_type: value,
          device_type_value: '스피커',
        }));
        // } else if (value === '221002') {
        //   setDeviceInputForm((prev) => ({
        //     ...prev,
        //     device_type: value,
        //     device_type_value: '전광판',
        //   }));
      } else if (value === '221003') {
        setDeviceInputForm((prev) => ({
          ...prev,
          device_type: value,
          device_type_value: '로고젝터',
        }));
      } else if (value === '221004') {
        setDeviceInputForm((prev) => ({
          ...prev,
          device_type: value,
          device_type_value: 'LED 투광등',
        }));
      }
    } else if (name === 'device_operation_name_form') {
      setDeviceInputForm((prev) => ({
        ...prev,
        name: value,
      }));
    } else if (name === 'device_description_form') {
      setDeviceInputForm((prev) => ({
        ...prev,
        description: value,
      }));
    }

    // improve code
    if (isNewClicked) {
      setHasChangesCreate(true);
    } else {
      setHasChangesUpdate(true);
    }

    //divce_type - type , name - operation_name , description - operation_description
  };

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    index: 'id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    movableRows: false,
    resizableRows: false,
    selectableRows: false, //이 옵션을 줘야 로우 선택이 된다.
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const handleSearchInit = (inputVal: string = '', _radio?: string, _radioUsage?: string) => {
    table.setFilters({
      device_type: deviceTypeFilter || undefined,
      input: inputVal || undefined,
    });
  };

  const handleSearchReset = () => {
    setDeviceTypeFilter('');
    table.reset();
  };

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(
          t('입력하신 내용이 저장되지 않습니다. 계속하시겠습니까?'),
          {
            mode: 'confirm',
          }
        );
        message
          .confirmClicked()
          .then(() => {
            const rowData = row.getData();
            tbRef.current?.deselectRow();
            row.select();
            row.getElement().classList.add('custom-hover');

            setSelectedDevice({
              device_operation_id: rowData.device_operation_id,
            });
            setDisabledForm(false);
            setIsDeviceTypeSelected(!!rowData.device_type);
            enableUpdateButtons();
            setHasChangesUpdate(false);
            setHasChangesCreate(false);
            setIsNewClicked(false);
          })
          .catch(() => {
            row.deselect();

            const prevId = selectedDeviceRef.current?.device_operation_id;
            if (prevId) {
              const prevRow = tbRef.current?.getRow(prevId);
              prevRow?.select();
              prevRow?.getElement().classList.add('custom-hover');
              setDisabledForm(false);
            }
          });
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedDevice({
          device_operation_id: rowData.device_operation_id,
        });

        setIsDeviceTypeSelected(!!rowData.device_type);
        setDisabledForm(false);
        enableUpdateButtons();
        setIsNewClicked(false);
      }

      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [enableUpdateButtons, t]
  );

  const handleNewButtonClick = () => {
    //alert('생성!')
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true); //등록으로 변경
        setDisabledForm(false);
        enableRegisterButtons();
        //enableInitialButtons()
        enableCancelButtons();
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      enableRegisterButtons();
      setIsNewClicked(true); //등록으로 변경
      setDisabledForm(false);
      // enableInitialButtons()
      enableCancelButtons();
    }

    setIsNewClicked(true); //등록으로 변경
    setHasChangesUpdate(false); //원래 변경안됨 상태로 초기화
    setSelectedDevice({ device_operation_id: null });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('입력하신 내용이 저장되지 않습니다. 계속하시겠습니까?'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
        setSelectedDevice({ device_operation_id: null });
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          tbRef.current?.deselectRow();
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
          setSelectedDevice({ device_operation_id: null });
        });
      } else {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setIsNewClicked(true);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedDevice({ device_operation_id: null });
      }
    } else {
      tbRef.current?.deselectRow();
      reloadCallback();
      setDisabledForm(true);
      emptyDetail();
      setSelectedDevice({ device_operation_id: null });
    }
  };
  // ------------------------------------------

  const handleRegistButtonClick = () => {
    //alert('등록!')
    //팝업창 말고 메인화면에서만 등록
    // deviceInputForm에서 값을 가져와 createObj에 매핑

    //입력필드 검증
    if (!validateRequiredFields(deviceInputForm)) return;

    const createObj = createDevicePayload(); //POST요청에 전달할 객체

    //alert(createObj.vms_dom);

    //alert('생성!')

    //createObj → FormData 변환
    const formData = createFormDataFromObject(createObj);

    for (const pair of formData.entries()) {
      console.log(pair[0], ':', pair[1]);
    }

    createDeviceOperationAndDom(formData as unknown as DeviceOperationUpdateDOMRequest); //POST API
    // reloadCallback();
    // setDisabledForm(true);
  };

  //필수 입력값이 누락되었는지 검증하는 함수
  //Validates required fields for device registration
  const validateRequiredFields = ({
    device_type,
    name,
    description: _description,
    is_device_not_set,
  }: DeviceInputFormState) => {
    const missing: string[] = [];
    if (!device_type) missing.push('device_type');
    if (!name) missing.push('name');
    //if (!description) missing.push("description");

    if (is_device_not_set || missing.length > 0) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage(`필수 값을 모두 입력해주세요: ${missing.join(', ')}`);
      console.warn('누락된 필수 입력값:', missing.join(', '));
      return false;
    }

    return true;
  };

  //기기 유형(device_type)에 따라 기기 등록을 위한 payload 객체를 생성한다.
  //Generates a payload object for device registration based on the device_type.
  const createDevicePayload = () => {
    // 기본값 설정 (스피커/VMS 공통 필드)
    // Base values (common fields for both speaker and VMS)
    const base = {
      device_type: deviceInputForm.device_type,
      name: deviceInputForm.name || '',
      description: deviceInputForm.description || '',
      // VMS 관련 기본값 / Default VMS-related fields
      vms_skin_type: initialVmsInputForm.vms_skin_type,
      vms_dom: initialVmsInputForm.vms_dom,
      vms_text_orientation: initialVmsInputForm.vms_text_orientation,
      vms_skin_image: initialVmsInputForm.vms_skin_image,
      vms_skin_image_path: initialVmsInputForm.vms_skin_image_path,
      vms_skin_option: initialVmsInputForm.vms_skin_option,
      vms_exposure_time: initialVmsInputForm.vms_exposure_time,

      vms_text: initialVmsInputForm.vms_text,
      vms_text_size: initialVmsInputForm.vms_text_size,
      vms_text_color: initialVmsInputForm.vms_text_color,
      vms_text_weight: initialVmsInputForm.vms_text_weight,

      vms_text2: initialVmsInputForm.vms_text2,
      vms_text2_size: initialVmsInputForm.vms_text2_size,
      vms_text2_color: initialVmsInputForm.vms_text2_color,
      vms_text2_weight: initialVmsInputForm.vms_text2_weight,

      vms_width: initialVmsInputForm.vms_width,
      vms_height: initialVmsInputForm.vms_height,

      vms_background_color: initialVmsInputForm.vms_background_color,

      // 스피커 관련 기본값 / Default speaker-related fields
      speaker_file: initialSpeakerInputForm.speaker_file,
      speaker_file_path: initialSpeakerInputForm.speaker_file_path,
      speaker_schedule: initialSpeakerInputForm.speaker_schedule,
    };

    const deviceType = deviceInputForm.device_type;

    // 1. 스피커인 경우 추가 필드 가공 / If device is speaker
    if (deviceType === '221001') {
      return {
        ...base,
        speaker_file_path: `/data/speaker/${speakerInputForm.speaker_file}.wav`,
        speaker_schedule: (speakerInputForm.speaker_schedule || []).map(
          ({ is_new, ...rest }) => rest
        ),
      };
    }

    // 2. VMS인 경우 VMS 세부값 덮어쓰기 / If device is VMS, override VMS-specific fields
    // 전광판(221002) 비활성 - DB에서 전광판 코드 제거됨
    if (deviceType === '221002') {
      return {
        ...base,
        vms_skin_type: vmsInputForm.vms_skin_type || '',
        vms_dom: vmsInputForm.vms_dom || '',
        vms_text_orientation: vmsInputForm.vms_text_orientation || 'VMS_TO_HOR',
        vms_skin_option: vmsInputForm.vms_skin_option || '',
        vms_skin_image: vmsInputForm.vms_skin_image || '',
        vms_skin_file_name: vmsInputForm.vms_skin_file_name || '',
        vms_exposure_time: vmsInputForm.vms_exposure_time || 180,

        vms_text: vmsInputForm.vms_text || '',
        vms_text_size: vmsInputForm.vms_text_size || 30,
        vms_text_color: vmsInputForm.vms_text_color || '#000000',
        vms_text_weight: vmsInputForm.vms_text_weight || 'normal',

        vms_text2: vmsInputForm.vms_text2 || '',
        vms_text2_size: vmsInputForm.vms_text2_size || 30,
        vms_text2_color: vmsInputForm.vms_text2_color || '#000000',
        vms_text2_weight: vmsInputForm.vms_text2_weight || 'normal',

        vms_width: vmsInputForm.vms_width || 288,
        vms_height: vmsInputForm.vms_height || 96,

        vms_background_color: vmsInputForm.vms_background_color,
      };
    }

    // 기본 기기 (스피커/VMS 외) - 공통 값만 반환
    // Default case (non-speaker/VMS) - return base only
    return base;
  };

  //객체 데이터를 FormData 형식으로 변환하는 함수
  const createFormDataFromObject = (obj: Record<string, unknown>) => {
    const formData = new FormData();

    for (const key in obj) {
      const value = obj[key];

      if (key === 'vms_skin_image') {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'string' && value.startsWith('data:image')) {
          const mimeType = value.match(/data:(.*);base64,/)?.[1] ?? '';
          const blob = base64ToBlob(value, mimeType);
          const fileName = (obj.vms_skin_file_name as string) || 'skin_image.png';
          formData.append(key, blob, fileName);
        } else {
          formData.append(key, value as string);
        }
      } else if (key === 'speaker_schedule' && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, (value ?? '') as string);
      }
    }

    return formData;
  };

  const base64ToBlob = (base64: string, mimeType: string) => {
    const base64Data = base64.replace(/^data:\w+\/\w+;base64,/, '');
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handleConfirmButtonClick = () => {
    //alert('확인!')
    //alert(vmsInputForm.vms_dom)

    const updateObj: Record<string, any> = {
      device_operation_id: selectedDevice.device_operation_id,
      device_type: deviceInputForm.device_type, // 기기유형
      name: deviceInputForm.name || '', // 동작명
      description: deviceInputForm.description || '', // 동작설명
    };

    // device_type에 따라 speaker 또는 vms 관련 폼을 그대로 추가
    if (deviceInputForm.device_type === '221001') {
      // speaker일 때
      updateObj.speaker_file = speakerInputForm.speaker_file;
      updateObj.speaker_file_path = speakerInputForm.speaker_file_path;
      updateObj.speaker_schedule = (speakerInputForm.speaker_schedule || []).map(
        ({ is_new, ...rest }) => rest
      );
    } else if (deviceInputForm.device_type === '221002') {
      // vms일 때 - 전광판(221002) 비활성 - DB에서 전광판 코드 제거됨
      updateObj.vms_skin_type = vmsInputForm.vms_skin_type;
      updateObj.vms_dom = vmsInputForm.vms_dom;
      updateObj.vms_text_orientation = vmsInputForm.vms_text_orientation;

      updateObj.vms_skin_option = vmsInputForm.vms_skin_option;
      updateObj.vms_exposure_time = vmsInputForm.vms_exposure_time;

      updateObj.vms_text = vmsInputForm.vms_text;
      updateObj.vms_text_size = vmsInputForm.vms_text_size;
      updateObj.vms_text_color = vmsInputForm.vms_text_color;
      updateObj.vms_text_weight = vmsInputForm.vms_text_weight;

      updateObj.vms_text2 = vmsInputForm.vms_text2;
      updateObj.vms_text2_size = vmsInputForm.vms_text2_size;
      updateObj.vms_text2_color = vmsInputForm.vms_text2_color;
      updateObj.vms_text2_weight = vmsInputForm.vms_text2_weight;

      updateObj.vms_width = vmsInputForm.vms_width;
      updateObj.vms_height = vmsInputForm.vms_height;

      updateObj.vms_background_color = vmsInputForm.vms_background_color;

      updateObj.vms_skin_image = vmsInputForm.vms_skin_image;
      updateObj.vms_skin_option_image = vmsInputForm.vms_skin_option_image;
      updateObj.vms_skin_image_path = vmsInputForm.vms_skin_image_path;
    }

    const formData = new FormData();

    for (const key in updateObj) {
      const value = updateObj[key];

      if (key === 'vms_skin_image') {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'string' && value.startsWith('data:image')) {
          const mimeType = value.match(/data:(.*);base64,/)?.[1] ?? '';
          const blob = base64ToBlob(value, mimeType);

          const originalFileName = updateObj.vms_skin_file_name || 'skin_image.png';
          formData.append(key, blob, originalFileName);
        } else {
          formData.append(key, value);
        }
      } else if (key === 'speaker_schedule' && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else if (typeof value === 'object' && value !== null) {
        // 일반 객체면 JSON으로
        formData.append(key, JSON.stringify(value));
      } else {
        // 기본값
        formData.append(key, value ?? '');
      }
    }

    // 디버깅용 출력
    // 최종 수정용 폼 데이터
    for (const pair of formData.entries()) {
      console.log(`${pair[0]}:`, pair[1]);
    }

    // 이후 로직
    setDisabledForm(false);
    setHasChangesUpdate(false);
    enableUpdateButtons();
    setIsNewClicked(false);

    updateDeviceOperationAndDom(formData as unknown as DeviceOperationUpdateDOMRequest);
  };

  const handleDeleteButtonClick = () => {
    if (!selectedDevice?.device_operation_id) {
      //alert('삭제할 항목이 선택되지 않았습니다.');
      // TODO: i18n - hardcoded Korean string
      console.log('삭제할 항목이 선택되지 않았습니다.');
      return;
    }

    //alert('삭제!');

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('정말로 삭제하시겠습니까?'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      //alert(deviceInputForm.mapped_scenario_name)
      if (deviceInputForm.mapped_scenario_name !== null) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        // TODO: i18n - hardcoded Korean string
        new NoticeMessage(
          '해당 함체에 매핑된 사이트 or 접근로가 존재합니다. 먼저 매핑을 해제해주세요'
        );
        return;
      }

      const idToDelete = selectedDevice.device_operation_id;
      if (idToDelete != null) {
        deleteDeviceOperation(idToDelete);
      }
      // reloadCallback();
    });
  };

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          {/* TODO: i18n - hardcoded Korean string */}
          <h3>시나리오 관리</h3>
          <h3>&gt;</h3>
          <h3>기기 동작 관리</h3>
        </div>

        <ContainerCard>
          {/* TODO: i18n - hardcoded Korean string */}
          <Filtering
            labelSelect="기기 유형"
            placeholder="동작명, 동작설명"
            disableFiltering={true}
            onSearch={handleSearchInit}
            onReset={handleSearchReset}
          >
            <Select
              className="cursor-pointer"
              options={deviceTypeFilteroptions}
              value={deviceTypeFilter}
              onChange={handleDeviceTypeFilterChange}
            />
          </Filtering>
        </ContainerCard>

        <ContainerCard>
          <ReactTabulator
            data={deviceRows}
            columns={deviceColumns}
            layout={'fitColumns'}
            id="scenario-device-table"
            className="tabulator-custom w-full "
            //   pagination="local"
            options={optionsTabulator}
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              // Header click → delegate sorting to the server (see useServerTable).
              dataSorted: remoteSortHandler(table, DEVICE_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (selectedDevice?.device_operation_id) {
                  tbRef.current?.getRows().forEach((row) => {
                    const rowData = row.getData();
                    if (rowData.device_operation_id === selectedDevice.device_operation_id) {
                      row.select();
                      row.getElement().classList.add('custom-hover');
                    }
                  });
                } else if (newId) {
                  tbRef.current?.getRows().forEach((row) => {
                    const rowData = row.getData();
                    if (rowData.device_operation_id === newId) {
                      row.select();
                      row.getElement().classList.add('custom-hover');
                      tbRef.current?.selectRow(newId as any);
                      handleRowSelected(row);
                    }
                  });
                }
              },
            }}
          />
          <TablePagination
            total={devicePageData?.total ?? 0}
            page={table.page}
            size={table.size}
            totalPages={devicePageData?.total_pages ?? 0}
            onPageChange={table.setPage}
          />
        </ContainerCard>

        <ContainerCard className=" flex flex-col">
          <div className="box-scenario-event flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-[50px]">
              {/* TODO: i18n - hardcoded Korean string */}
              <DetailForm
                className="items-center!"
                label="기기 동작 ID"
                value={deviceInputForm?.device_operation_id}
                name={'deivce_opeartion_id_form'}
                disabled={true}
              />

              <DetailForm
                inputType="select"
                className="items-center!"
                label="기기 유형"
                required={true}
                optionSelect={deviceTypeFormoptions}
                value={deviceInputForm?.device_type}
                disabled={disabledForm}
                name={'device_type_form'}
                onChange={(e) => handleDeviceTypeFormChange(e)}
              />

              <DetailForm
                className="items-center!"
                label="동작명"
                name={'device_operation_name_form'}
                value={deviceInputForm?.name}
                disabled={disabledForm}
                onChange={(e) => handleDeviceTypeFormChange(e)}
                required={true}
              />
            </div>

            <div className="grid grid-cols-3 gap-[50px]">
              <DetailForm
                className="items-center!"
                label="동작 설정"
                required={true}
                showInput={false}
                disabled={disabledForm}
              >
                <div className="w-[300px]">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDeviceTypeSelected || disabledForm) return;
                      handleDeviceOperationAction();
                    }}
                    disabled={!isDeviceTypeSelected || disabledForm}
                    className={`body1bold px-[20px] py-[3px] text-[#FEFEFE] ${
                      !isDeviceTypeSelected || disabledForm
                        ? 'cursor-not-allowed text-gray-400'
                        : 'cursor-pointer text-blue-500'
                    }`}
                    style={{ textDecoration: 'underline' }}
                  >
                    {deviceInputForm.is_device_not_set ? '미설정' : '미리보기'}
                  </button>
                </div>
              </DetailForm>
            </div>

            <div className="grid grid-cols-1">
              <DetailForm
                className=" h-[90px]"
                inputType="textarea"
                label="동작 설명"
                disabled={disabledForm}
                name={'device_description_form'}
                value={deviceInputForm?.description}
                onChange={(e) => handleDeviceTypeFormChange(e)}
                maxLength={500}
              />
            </div>

            <hr className="border-t border-gray-300" />
            <div className="flex items-center justify-between gap-4 w-full">
              <div className="flex-1">
                <LogList logs={logData} />
              </div>
              <div className="flex-none">
                <ButtonGroup
                  onClickDelete={handleDeleteButtonClick}
                  onClickNew={handleNewButtonClick}
                  onClickCancel={handleCancelButtonClick}
                  onClickRegist={handleRegistButtonClick}
                  onClickConfirm={handleConfirmButtonClick}
                  isNewClicked={isNewClicked}
                  cancelButtonState={buttonState.cancel}
                  confirmButtonState={
                    hasChangesUpdate && !isNewClicked ? false : buttonState.confirm
                  }
                  //confirmButtonState 가 true일때 확인 비활성화
                  deleteButtonState={buttonState.delete}
                  newButtonState={hasChangesCreate ? false : buttonState.create}
                />
              </div>
            </div>
          </div>
        </ContainerCard>
      </section>

      {/* TODO: i18n - hardcoded Korean string */}

      {/* 전광판(221002) 비활성 - DB에서 전광판 코드 제거됨 */}
      {/* {deviceInputForm.device_type_value === '전광판' && (
        <ModalBoardOperation
          isOpen={isModalOpen}
          onChangeForm={handleVmsInputForm}
          vmsInputForm={
            vmsInputForm as unknown as React.ComponentProps<
              typeof ModalBoardOperation
            >['vmsInputForm']
          }
          removeFile={removeFile}
          onSave={handleSaveVmsSetting}
          onClose={handleCloseModal}
        />
      )} */}

      {deviceInputForm.device_type_value === '스피커' && ( //speaker
        <ModalSpeakerOperation
          onChangeForm={handleSpeakerInputForm}
          speakerInputForm={speakerInputForm}
          setSpeakerInputForm={
            setSpeakerInputForm as unknown as React.ComponentProps<
              typeof ModalSpeakerOperation
            >['setSpeakerInputForm']
          }
          speakerScheduleInputFormList={speakerScheduleInputFormList}
          //스피커 스케줄은 동적박스로 변경되므로 따로 관리해서 CRUD가 되어야함
          setSpeakerScheduleInputFormList={setSpeakerScheduleInputFormList}
          onSave={handleSaveSpeakerSetting}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}

      {deviceInputForm.device_type_value === '로고젝터' && (
        <ModalProjector
          isOpen={isModalOpen}
          goboImageId={(detailDeviceOperationData?.data as any)?.gobo_image_id ?? null}
          onClose={() => {
            setIsModalOpen(false);

            setDeviceInputForm((prev) => ({
              ...prev,
              is_device_not_set: false,
            }));
          }}
        />
      )}
    </>
  );
};

export default DeviceManagementPage;
